import React, { useState } from 'react';
import { motion } from 'motion/react';
import { X, Trophy, Calendar, Info } from 'lucide-react';
import { doc, collection, addDoc, setDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { PITCH_COUNT_RULESETS } from '../lib/pitch-count-rules';
import { cn } from '../lib/utils';

interface TournamentFormProps {
  teamId: string;
  /** Team settings — used to autofill Custom pitch rules from the season-level
      defaults (Settings → Tournaments → Custom) when no per-tournament rule exists. */
  teamSettings?: any;
  tournament?: any;
  onClose: () => void;
  onCreated: (tournamentId: string) => void;
}

export const TournamentForm = ({ teamId, teamSettings, tournament, onClose, onCreated }: TournamentFormProps) => {
  const [formData, setFormData] = useState({
    name: tournament?.name || '',
    startDate: tournament?.startDate || new Date().toISOString().split('T')[0],
    endDate: tournament?.endDate || new Date().toISOString().split('T')[0],
    format: tournament?.format || '2_pool_bracket',
    pitchCountRulesetOverride: tournament?.pitchCountRulesetOverride || '',
    // Custom pitch rules — autofill priority: this tournament's own values (when
    // editing) → the team's season-level Custom defaults → sensible fallbacks.
    // The coach can edit them on the fly even if no season defaults exist yet.
    customTournamentTracking: tournament?.customTournamentTracking ?? teamSettings?.customTournamentTracking ?? 'pitches',
    customTournamentDailyLimit: tournament?.customTournamentDailyLimit ?? teamSettings?.customTournamentDailyLimit ?? 85,
    customTournamentEventLimit: tournament?.customTournamentEventLimit ?? teamSettings?.customTournamentEventLimit ?? 0,
    customTournamentNoRestDays: tournament?.customTournamentNoRestDays ?? teamSettings?.customTournamentNoRestDays ?? false,
    poolPlayDefaults: tournament?.poolPlayDefaults || {
      pitchingStrategy: 'mid_tier',
      fairnessMode: 'balanced'
    },
    bracketPlayDefaults: tournament?.bracketPlayDefaults || {
      pitchingStrategy: 'best_available',
      fairnessMode: 'competitive'
    },
    enforceGlobalRest: tournament?.enforceGlobalRest ?? false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (tournament?.id) {
        await setDoc(doc(db, `teams/${teamId}/tournaments`, tournament.id), {
          ...formData,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        onCreated(tournament.id);
      } else {
        const docRef = await addDoc(collection(db, `teams/${teamId}/tournaments`), {
          ...formData,
          status: 'upcoming',
          createdAt: new Date().toISOString()
        });
        onCreated(docRef.id);
      }
    } catch (error) {
      handleFirestoreError(error, tournament?.id ? OperationType.UPDATE : OperationType.CREATE, `teams/${teamId}/tournaments`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-50 bg-surface overflow-y-auto p-6"
    >
      <div className="max-w-md mx-auto space-y-8 pb-12">
        <header className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gold rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(245,158,11,0.3)]">
              <Trophy className="w-6 h-6 text-surface" />
            </div>
            <h2 className="text-3xl font-black italic tracking-tight uppercase text-ink">
              {tournament ? 'Edit Tournament' : 'New Tournament'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-ink-2 hover:text-ink">
            <X className="w-6 h-6" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Tournament Name</label>
            <input 
              required
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className="w-full bg-canvas border-hairline rounded-xl p-4 outline-none focus:ring-1 focus:ring-amber-500 text-ink"
              placeholder="e.g. KC Classic Spring 2026"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-2" />
                <input 
                  required
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData({...formData, startDate: e.target.value})}
                  className="w-full bg-canvas border-hairline rounded-xl p-4 pl-12 outline-none focus:ring-1 focus:ring-amber-500 text-ink"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">End Date</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-2" />
                <input 
                  required
                  type="date"
                  value={formData.endDate}
                  onChange={e => setFormData({...formData, endDate: e.target.value})}
                  className="w-full bg-canvas border-hairline rounded-xl p-4 pl-12 outline-none focus:ring-1 focus:ring-amber-500 text-ink"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Format</label>
            <select 
              value={formData.format}
              onChange={e => setFormData({...formData, format: e.target.value})}
              className="w-full bg-canvas border-hairline rounded-xl p-4 outline-none focus:ring-1 focus:ring-amber-500 text-ink appearance-none"
            >
              <option value="2_pool_bracket">2 Pool + Bracket</option>
              <option value="3_pool_bracket">3 Pool + Bracket</option>
              <option value="round_robin">Round Robin</option>
              <option value="single_elimination">Single Elimination</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Tournament Pitch Count Rules</label>
            <select 
              value={formData.pitchCountRulesetOverride}
              onChange={e => setFormData({...formData, pitchCountRulesetOverride: e.target.value})}
              className="w-full bg-canvas border-hairline rounded-xl p-4 outline-none focus:ring-1 focus:ring-amber-500 text-ink appearance-none"
            >
              <option value="">Use Season Rules</option>
              {Object.values(PITCH_COUNT_RULESETS).map(ruleset => (
                <option key={ruleset.id} value={ruleset.id}>{ruleset.name}</option>
              ))}
            </select>
          </div>

          {/* Custom pitch rules — shown only when "Custom" is the selected ruleset.
              Fields are autofilled (tournament → season settings → defaults) and
              saved on this tournament doc, so each tournament can carry its own. */}
          {formData.pitchCountRulesetOverride === 'custom' && (
            <div className="p-5 bg-canvas rounded-2xl border border-hairline space-y-5">
              <div className="flex items-center justify-between border-b border-hairline pb-2">
                <h3 className="text-xs font-semibold text-gold-ink uppercase tracking-widest">Custom Pitch Rules</h3>
                {(teamSettings?.customTournamentTracking != null && tournament?.customTournamentTracking == null) && (
                  <span className="text-[9px] font-semibold text-ink-2 uppercase tracking-widest">From season defaults</span>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Tracking Unit</label>
                <select
                  value={formData.customTournamentTracking}
                  onChange={e => setFormData({ ...formData, customTournamentTracking: e.target.value })}
                  className="w-full bg-surface border border-hairline rounded-xl p-3 text-xs font-bold outline-none text-ink appearance-none"
                >
                  <option value="pitches">Pitches</option>
                  <option value="innings">Innings</option>
                  <option value="outs">Outs</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                  Daily Limit ({formData.customTournamentTracking} per day)
                </label>
                <input
                  type="number"
                  min={0}
                  value={formData.customTournamentDailyLimit}
                  onChange={e => setFormData({ ...formData, customTournamentDailyLimit: parseInt(e.target.value) || 0 })}
                  className="w-full bg-surface border border-hairline rounded-xl p-3 text-xs font-bold outline-none text-ink"
                  placeholder="85"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                  Event Limit ({formData.customTournamentTracking} per tournament · 0 = no limit)
                </label>
                <input
                  type="number"
                  min={0}
                  value={formData.customTournamentEventLimit}
                  onChange={e => setFormData({ ...formData, customTournamentEventLimit: parseInt(e.target.value) || 0 })}
                  className="w-full bg-surface border border-hairline rounded-xl p-3 text-xs font-bold outline-none text-ink"
                  placeholder="0"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="pr-3">
                  <p className="text-xs font-bold text-ink uppercase">No Rest Days Required</p>
                  <p className="text-[10px] text-ink-2 uppercase tracking-widest mt-1">Pitchers can appear any day without enforced rest.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, customTournamentNoRestDays: !formData.customTournamentNoRestDays })}
                  className={cn(
                    "w-12 h-6 rounded-full transition-colors relative shrink-0",
                    formData.customTournamentNoRestDays ? "bg-gold" : "bg-raised"
                  )}
                >
                  <div className={cn(
                    "w-4 h-4 bg-ink rounded-full absolute top-1 transition-transform shadow-sm",
                    formData.customTournamentNoRestDays ? "translate-x-7" : "translate-x-1"
                  )} />
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Pitching Mode</label>
            <div className="flex items-center justify-between p-4 bg-canvas border border-hairline rounded-xl">
              <div>
                <p className="text-xs font-bold text-ink uppercase">Fresh Start Mode</p>
                <p className="text-[10px] text-ink-2 uppercase tracking-widest mt-1">Ignore rest restrictions from league games</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({...formData, enforceGlobalRest: !formData.enforceGlobalRest})}
                className={cn(
                  "w-12 h-6 rounded-full transition-colors relative shrink-0",
                  !formData.enforceGlobalRest ? "bg-gold" : "bg-raised"
                )}
              >
                <div className={cn(
                  "w-4 h-4 bg-ink rounded-full absolute top-1 transition-transform shadow-sm",
                  !formData.enforceGlobalRest ? "translate-x-7" : "translate-x-1"
                )} />
              </button>
            </div>
          </div>

          <div className="p-6 bg-canvas rounded-3xl border border-hairline space-y-6">
            <h3 className="text-xs font-semibold text-gold-ink uppercase tracking-widest border-b border-hairline pb-2">Pool Play Defaults</h3>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Pitching Strategy</label>
                <select 
                  value={formData.poolPlayDefaults.pitchingStrategy}
                  onChange={e => setFormData({
                    ...formData, 
                    poolPlayDefaults: { ...formData.poolPlayDefaults, pitchingStrategy: e.target.value }
                  })}
                  className="w-full bg-surface border border-hairline rounded-xl p-3 text-xs font-bold outline-none text-ink"
                >
                  <option value="mid_tier">Mid-Tier Arms</option>
                  <option value="best_available">Best Available</option>
                  <option value="rotate">Rotate</option>
                </select>
              </div>

            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 bg-canvas rounded-2xl border border-hairline">
            <Info className="w-4 h-4 text-ink-2 mt-0.5" />
            <p className="text-[10px] font-medium text-ink-2 leading-relaxed italic">
              Bracket play defaults to best available pitcher and competitive lineup. You can adjust per game.
            </p>
          </div>

          <button 
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-gold text-surface font-black rounded-2xl shadow-[0_0_30px_rgba(245,158,11,0.2)] uppercase italic disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : tournament ? 'Update Tournament' : 'Create Tournament'}
          </button>
        </form>
      </div>
    </motion.div>
  );
};
